import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const guidelines = [
  {
    id: "profile",
    title: "Profile Guidelines",
    icon: "ri-user-smile-line",
    content: (
      <div className="flex flex-col gap-6 text-sm text-gray-500 leading-relaxed">
        <div className="flex flex-col gap-2">
          <p className="font-bold text-gray-800 uppercase tracking-tight">Age</p>
          <p>
            You need to be at least 18 years old to join Aurays. Creating a profile that intentionally misrepresents you
            as being under the age of 18 is not allowed. We reserve the right to ask for your ID to verify your age, and
            we&rsquo;ll block you from the platform if you&rsquo;re underage.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="font-bold text-gray-800 uppercase tracking-tight">Profile Photos</p>
          <p>
            We want your profile to celebrate your authentic self! That&rsquo;s why we require at least three of your
            profile photos to depict only you and to clearly show your full face. We do not permit:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>
              Profile photos that are heavily distorted or contain exaggerated or unnatural digital effects to the point
              where it cannot be clearly determined that you&rsquo;re the person in the photos
            </li>
            <li>Memes or photos with only — or primarily — text as a profile photo</li>
            <li>Profile photos of children on their own</li>
            <li>Profile photos with unclothed children</li>
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <p className="font-bold text-gray-800 uppercase tracking-tight">Username</p>
          <p>
            Members are allowed to use initials, abbreviations, contracted or shortened versions of their name,
            nicknames, full names, and middle names. Members do not have to use their legal name or full name, but
            usernames should be an authentic representation of the name you use in everyday life. We do not permit:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>Any words or phrases that violate our Community Guidelines</li>
            <li>Using the name of a celebrity or fictional character</li>
            <li>
              Words or characters (other than a valid name) including descriptive words, symbols (e.g. $, *, @,),
              emojis, numbers, or punctuation
            </li>
            <li>Social media or messaging platform details</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "nudity",
    title: "Adult Nudity and Sexual Activity",
    icon: "ri-rest-time-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We don&rsquo;t permit nude, sexually explicit, or sexually vulgar profile content. We also don&rsquo;t allow
          the commercial exchange of any romantic or sexual activity, content, or services, including attempts to sell,
          advertise, or buy adult sexual content.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="text-sm leading-relaxed mb-4">
            We know that some conversations on Aurays may discuss and involve sex, intimacy, romance, and attraction.
            Members using Aurays are allowed to:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc mb-4">
            <li>
              State their romantic or sexual intentions on their profiles, as long as it&rsquo;s legal and not overly
              obscene, crude, or otherwise in violation of these Community Guidelines.
            </li>
            <li>
              Use their private messages on the platform as a place to engage in sexual conversations with matches, as
              long as everything is consensual (that means welcomed by both parties), honest, and law abiding.
            </li>
          </ul>
          <p className="text-sm leading-relaxed mb-4">
            We value self-expression and diversity within our community and encourage our members to share profile
            photos that celebrate bodies of all shapes and sizes. Photos of members wearing athleticwear are permitted
            whether indoors or outdoors. Members are permitted to wear swimwear in profile photos if taken outdoors.
          </p>
          <p className="font-bold text-gray-800 mb-2">
            However, we don&rsquo;t allow profile photos or the sharing of links on profiles that depict:
          </p>
          <p className="mb-2">
            People where the following is shown, even if covered or partially covered by emojis, hair or transparent
            clothing:
          </p>
          <ul className="flex flex-col gap-1.5 pl-4 list-disc mb-4">
            <li>Genitals or pubic regions</li>
            <li>Bare buttocks</li>
            <li>Women&rsquo;s nipples and areolas</li>
            <li>Visible explicit outlines of genitals</li>
            <li>People in lingerie or underwear</li>
            <li>Sexual acts of any kind, even if not explicitly visible</li>
            <li>
              Digitally-created or drawn imagery (e.g. cartoons, memes, anime, drawings) that depicts any of the above.
              This imagery doesn&rsquo;t necessarily have to include human beings
            </li>
          </ul>
          <p className="font-bold text-gray-800 mb-2">
            We also don&rsquo;t allow Aurays to be used for any commercial exchange of sexual or romantic acts of any
            kind. This includes, but isn&rsquo;t limited to, the following:
          </p>
          <ul className="flex flex-col gap-1.5 pl-4 list-disc">
            <li>
              Any attempts to sell, advertise, or buy sexual acts in exchange for money or something of value (e.g. sex
              work, escort services)
            </li>
            <li>
              Any attempts to sell, advertise, or buy adult sexual content (e.g. pornography, sexual live
              streams/webcamming). This includes sharing links to third-party websites that promote or feature adult
              sexual content.
            </li>
            <li>
              The use of the App to promote or facilitate non-consensual sexual acts, including the sharing of intimate
              images without consent.
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "bullying",
    title: "Bullying and Abusive Conduct",
    icon: "ri-user-voice-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          Our community is for creating kind connections. We don&rsquo;t allow content or behavior that makes any
          individual or group feel harassed, bullied, or targeted. This includes belittling, insulting, or intimidating
          behavior; making unsolicited comments about someone&rsquo;s appearance; engaging in emotional abuse;
          blackmail; repeated unwanted contact; or wishing, encouraging, or praising acts of violence.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">Members may not:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>
              Engage in any bullying, belittling, insulting, mocking, trolling, intimidating, dehumanizing, or degrading
              behavior
            </li>
            <li>Make harmful unsolicited comments about someone&rsquo;s body or appearance (e.g. body shaming)</li>
            <li>
              Wish, encourage, or praise acts of violence, or threatening acts of violence that would result in
              non-serious bodily injury
            </li>
            <li>Engage in emotional abuse and/or manipulation</li>
            <li>Repeat unwanted online contact of any kind</li>
            <li>
              Blame, shame, or target someone based on their status as a survivor/victim of sexual assault, sexual
              exploitation, sexual harassment, or domestic abuse
            </li>
            <li>
              Release a person&rsquo;s private contact information, photos, or other personally identifying information
              without their consent
            </li>
            <li>
              Blackmail a member to reveal embarrassing, compromising or otherwise private information (not including
              sexual or intimate content)
            </li>
            <li>Not show up to an in-person meet up despite clear plans agreed by both parties</li>
            <li>Create a profile with malicious intent to publish defamatory content about a person</li>
            <li>
              Share content that is disrespectful toward victims, survivors, or families impacted by suicide,
              self-injury, or eating disorders
            </li>
          </ul>
          <p className="mt-4 italic">
            This includes content about public figures. Members are permitted to express their criticism of public
            figures, but we will not tolerate abusive conduct in any form.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "child-safety",
    title: "Child Sexual Exploitation and Abuse",
    icon: "ri-mickey-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We have a zero tolerance policy towards any form of child sexual exploitation and abuse. We don&rsquo;t allow
          content that sexualizes or endangers children, real or fictional (e.g. anime, media, text, illustrations, or
          digital images). This includes any visual depictions or discussions of sexually explicit conduct involving a
          child. For the purposes of this policy, a child is anyone under the age of 18. It&rsquo;s prohibited to
          upload, store, produce, share, or entice anyone to share child sexual abuse material, even if the intent is to
          express outrage or raise awareness about this issue.
        </p>
        <div className="bg-red-50 p-6 rounded-2xl border border-red-100 text-red-900">
          <p className="mb-3">
            We understand that members might want to talk about and share images of their children with their
            connections. Any discussions about children must remain appropriate, relevant, and lawful. In addition, if
            you decide to include photos of children in your profile, they must be completely clothed and they cannot
            appear alone in the image. This includes any photos from your childhood. We don&rsquo;t allow profile photos
            of babies&rsquo;, toddlers&rsquo; or children&rsquo;s genitalia or buttocks even when they&rsquo;re covered
            or partially covered by emojis.
          </p>
          <p className="font-bold mb-2">Zero Tolerance Policy:</p>
          <p className="mb-3">
            Any violation of this policy will result in the immediate removal of the member from the App, termination of
            their account without refund, and potential reporting to law enforcement authorities.
          </p>
          <p>
            In compliance with the Crimes Ordinance (Cap. 200, Laws of Hong Kong) and other applicable local
            regulations, if we become aware of, detect, or suspect any material or behavior involving child sexual
            exploitation, child grooming, or child abuse, we will preserve all relevant data and immediately report the
            incident to the Hong Kong Police Force and any other competent local or global law enforcement authorities.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "commercial",
    title: "Commercial and Promotional Activity",
    icon: "ri-megaphone-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          Our platform is not a marketplace. We don&rsquo;t allow using Aurays for unsolicited commercial or promotional
          purposes, unless by our approved partners.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">The following behaviors aren&rsquo;t allowed on Aurays:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>
              Any unsolicited or irrelevant advertising or promotion of a product, brand, service, event, investment,
              business/organization, music, or performance
            </li>
            <li>
              Any group sales recruitment or promotion of a group sales product (multi-level marketing or pyramid
              schemes)
            </li>
            <li>Unsolicited promotion of your social media or website in private messages to other members</li>
            <li>Sharing financial account information, links, or usernames to money transfer apps in profiles</li>
            <li>
              Setting up accounts for the sole purpose of promoting a political party or electoral candidate in the
              context of an election
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "substances",
    title: "Controlled Goods and Substances",
    icon: "ri-capsule-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We don&rsquo;t allow members to use our platforms to buy, sell, supply, distribute, promote or facilitate the
          purchase, sale, supply, or distribution of illegal drugs and/or controlled goods and substances. This
          includes: e-cigarettes, marijuana, drug paraphernalia, or the misuse of legal substances like prescription
          drugs, tobacco, or alcohol.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">This includes, but isn&rsquo;t limited to:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>
              Content that depicts or promotes the use of illegal drugs, or misuse of other controlled goods and
              substances
            </li>
            <li>The purchase, sale, trade, or solicitation of drugs or other controlled substances</li>
            <li>
              Content that provides information on how to buy, sell, or illegally obtain drugs or controlled goods and
              substances
            </li>
            <li>
              Content providing instructions on how to use or manufacture illegal drugs or controlled goods and
              substances
            </li>
          </ul>
          <p className="mt-4">
            In private messages or group chats, we may also allow imagery or discussion of drugs if shared explicitly
            for educational or health-related purposes. However, it&rsquo;s important to make your intentions clear in
            sharing this content, or it may be removed and/or action may be taken against your account.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "terrorism",
    title: "Terrorism and Violent Extremism",
    icon: "ri-skull-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We don&rsquo;t allow content or behavior that promotes, incites, glorifies, or supports terrorist or violent
          extremist groups and individuals and their activities on Aurays.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">This includes, but isn&rsquo;t limited to:</p>
          <ul className="flex flex-col gap-3 pl-4 list-disc">
            <li>
              Expressions of support for designated (or known) terrorist and violent extremist groups, as well as
              support and promotion of their ideologies, activities, manuals, manifestos, publications, and propaganda.
            </li>
            <li>
              Dissemination of terrorist-related content, including gestures, imagery, symbols, flags, or slogans of
              terrorist or violent extremist groups, or individuals or groups that represent or are affiliated with
              them.
            </li>
            <li>
              Conspiring with, recruiting, or inciting a person or group to participate, commit, or contribute to
              terrorist or violent extremist activities or criminal acts on or off-platform.
            </li>
            <li>
              Glorification of violence and positive portrayals/depictions (including filming and livestreaming) of
              terrorist acts or perpetrators, threats of attacks, online fundraising, or facilitation of training or
              material support to terrorist or violent extremist organizations.
            </li>
            <li>
              Support for or affiliation with terrorist, organized groups, or criminal organizations that promote or use
              intimidation, violent extremism, and other criminal acts against civilians to further their causes
              (including political aims), either on or off the platform.
            </li>
          </ul>
          <p className="mt-4">
            We understand that members might discuss or share content about terrorism or violent extremism, for purposes
            of awareness, education, or condemnation. While we may allow this in private messages and group chats,
            it&rsquo;s important to make your intentions clear in sharing this content and not to share any graphic
            content. Otherwise, it may lead to your content being removed, action being taken against your account,
            and/or reporting to appropriate authorities.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "hate",
    title: "Identity-Based Hate",
    icon: "ri-heart-add-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We aim to foster a diverse and inclusive community on Aurays. We prohibit content or behavior that promotes or
          condones hate, dehumanization, degradation, or contempt against marginalized or minoritized communities based
          on the following protected attributes: race/ethnicity, national origin/nationality/immigration status, caste,
          sex, gender identity or expression, sexual orientation, disability or serious health condition, or
          religion/belief.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">This includes, but isn&rsquo;t limited to:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>
              Expressing threats, praises, or wishes of violence, death, or serious harm referencing protected
              attributes
            </li>
            <li>Using identity-based slurs or epithets to cause harm</li>
            <li>
              Supporting or glorifying known hate groups or sharing content, symbols, gestures, or imagery associated
              with hate groups
            </li>
            <li>
              Insulting, expressing contempt, advocating harm, using degrading or disparaging language (including
              harmful stereotypes, expressions of superiority, inferiority, or dismissal), mocking, or taunting others
              based on a protected attribute
            </li>
            <li>Denying the concept or severity of hate crimes or historical events</li>
            <li>
              Misgendering (i.e. intentionally using someone&rsquo;s wrong pronouns or gender) or dead-naming (i.e.
              intentionally calling someone by a name they no longer identify with)
            </li>
            <li>
              Intentionally reporting members to Aurays for no reason other than their gender identity or other
              protected attributes
            </li>
          </ul>
          <p className="mt-4">
            This also includes identity-based hate against public figures and people who aren&rsquo;t members of our
            community. While you may express the protected attributes of matches that you are looking for on your
            profile, we don&rsquo;t allow you to express protected attributes of matches that you aren&rsquo;t
            interested in. For example: You may state &ldquo;I&rsquo;m looking for a Muslim partner,&rdquo; but you may
            not state &ldquo;No Jews or Christians.&rdquo; We don&rsquo;t allow any preferences around someone&rsquo;s
            caste to be shared on profiles.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "inauthentic",
    title: "Inauthentic Profiles",
    icon: "ri-fingerprint-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          Aurays celebrates authenticity, and we expect all our members to represent themselves accurately on their
          profile. We don&rsquo;t allow impersonation or misrepresentation on our platform. This includes catfishing
          (i.e. creating an online persona that isn&rsquo;t you) or falsely stating facts about yourself (including
          name, gender, age, and permanent location).
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            Members are allowed to use initials, abbreviations, contracted/shortened versions of their name or middle
            name as long as these names are a variation of their authentic name. We don&rsquo;t allow impersonation or
            misrepresentation on our platform. We consider these, and similar behaviors, as inauthentic behavior. This
            includes, but isn&rsquo;t limited to:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>Catfishing (i.e. creating an online persona that isn&rsquo;t you)</li>
            <li>
              Using someone else&rsquo;s photos, artificially-generated photos, or enhanced photos to deceive others
            </li>
            <li>
              Misrepresenting, misleading or falsely stating facts about yourself (including name, gender, age, and
              permanent location)
            </li>
            <li>Pretending to be another person, including a celebrity or other public figure</li>
            <li>Allowing someone else to have access to -or control of- an account that does not represent them</li>
            <li>
              Sharing content that may infringe on someone else&rsquo;s copyright, trademark, name, likeness, image,
              other intellectual property, or privacy.
            </li>
            <li>Using profile photos that contain a QR code or URL</li>
            <li>Uploading profile photos of internet memes or images that are primarily text</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "misinformation",
    title: "Misinformation",
    icon: "ri-error-warning-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We prohibit the sharing of demonstrably false or substantially misleading content that&rsquo;s likely to cause
          serious harm or negatively impact individual or public safety. This includes content that directly contradicts
          information and guidance from leading and reputable global health organizations and public health authorities,
          false or misleading information on any civic process, and dangerous and unproven conspiracy-type theories.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            Misinformation is false or misleading content that can quickly escalate into serious risks to individuals,
            communities, and institutions. Because of this, we don&rsquo;t allow the sharing of demonstrably false or
            substantially misleading content that&rsquo;s likely to cause serious harm or negatively impact individual
            or public safety. This includes, but isn&rsquo;t limited to:
          </p>
          <ul className="flex flex-col gap-3 pl-4 list-disc mb-4">
            <li>
              Content that directly contradicts information and guidance from leading and reputable global health
              organizations and public health authorities that could result in injury or harm to the public, for example
              about COVID-19, monkeypox, or any other communicable disease or illness
            </li>
            <li>
              Any proposed cures for COVID-19 or any other illnesses not widely recognized as effective by leading and
              reputable global health organizations and public health authorities
            </li>
            <li>
              Demonstrably false or substantially misleading information regarding abortion care that is likely to cause
              serious harm. This does not prohibit members from voicing personal opinions or policy perspectives on the
              issue, as long as the information doesn&rsquo;t intend to harm, mislead, or contradict leading and
              reputable public health authorities and the discussion remains respectful and doesn&rsquo;t violate our
              Bullying and Abusive Conduct policy
            </li>
            <li>
              False claims or misleading information on any civic process (e.g. elections, voting systems, census,
              referendums, etc.)
            </li>
            <li>
              Dangerous and unproven conspiracy-type theories that may lead to individual or public safety concerns
            </li>
            <li>Content from or links to third party sites that are known to produce or contain misinformation</li>
            <li>Content that denies the concept or severity of hate crimes or historical events</li>
            <li>
              Any type of misinformation relating to political affairs aiming to influence political outcomes, or
              manipulate historical fact.
            </li>
            <li>
              Content that has been intentionally manipulated or taken out of context to spread false or misleading
              information.
            </li>
          </ul>
          <p className="italic">
            Sharing any of the above content may lead to your content being removed and/or action being taken against
            your account.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "violence",
    title: "Physical and Sexual Violence",
    icon: "ri-thunderstorms-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We don&rsquo;t tolerate any content, imagery, or behavior that commits or threatens credible acts of physical
          or sexual violence. This includes physical stalking, using our platform to assist, facilitate or support
          exploitation or human trafficking, and sexual assault of any kind, which we define as unwanted physical
          contact or attempted physical contact that is sexual in nature.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">This includes, but isn&rsquo;t limited to:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc mb-4">
            <li>Threatening or committing any type of physical harm to another person</li>
            <li>
              Threatening or committing sexual assault of any kind, which we define as any unwanted physical contact or
              attempted physical contact that is sexual in nature. These behaviors can range from:
              <ul className="flex flex-col gap-1 pl-6 list-circle mt-1">
                <li>Unwanted sexual touching or kissing of any body part, or attempts to do so</li>
                <li>The unwanted removal of another&rsquo;s clothes</li>
                <li>Stealthing (i.e. the non-consensual removal of barrier contraceptives)</li>
                <li>
                  Non-consensual sexual penetration or attempts to do so, which include but aren&rsquo;t limited to:
                  rape, non-consensual oral sex, sexual acts while one party is unconscious or unable to consent to
                  sexual activity, or sexual acts induced by coercion or a substance
                </li>
              </ul>
            </li>
            <li>Expressing credible threats of violence that would result in serious bodily injury or death</li>
            <li>
              Physical stalking (i.e. surveilling or showing up at someone&rsquo;s location in order to intimidate or
              initiate unwanted contact)
            </li>
            <li>
              Using our platform to facilitate exploitation or human trafficking. We define human trafficking as
              &ldquo;the recruitment, transportation, transfer, harbouring or receipt of adults through force, fraud or
              deception, with the aim of exploiting them for profit.&rdquo; (UN Office on Drugs and Crime)
            </li>
          </ul>
          <p className="font-bold text-gray-800 mb-2">Reporting and Enforcement:</p>
          <p className="mb-4">
            We strongly encourage our members to report this type of behavior if it occurs on the platform, in person
            between members, or elsewhere outside of the Aurays platform. Our specialized safety team can provide
            external resources for support. Since this behavior may also be criminal in nature, reporting to a local law
            enforcement authority may also be an option.
          </p>
          <p className="mb-4">
            If we become aware of any of the above conduct, we may take action against offending member accounts, and
            may also cooperate with law enforcement in any resulting criminal investigations.
          </p>
          <p className="italic">
            For more information about how you can remain safe while meeting a match in-person, you can visit the Aurays
            Help Centre.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "scams",
    title: "Scams, Fraud, and Theft",
    icon: "ri-shield-flash-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          Aurays prohibits any scam, fraud or theft activity intended to defraud or manipulate members out of financial
          or material resources. This includes requesting or seeking financial support, lying about your intentions for
          financial gain, or faking romantic intentions to deceive members out of financial or material resources.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            Aurays strictly prohibits any scam, fraud, or theft activity intended to defraud or manipulate members out
            of financial or material resources. We define scams as members making seemingly dishonest or false
            representations that aim to manipulate or defraud victims for financial gain or cause them loss. This
            includes, but isn&rsquo;t limited to:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>Asking other members for financial support in the form of money, loans, investments, or donations</li>
            <li>Lying about your intentions for financial gain</li>
            <li>
              Misrepresenting or faking romantic intentions to manipulate or deceive members out of financial or
              material resources
            </li>
            <li>
              Misrepresenting or falsely stating facts about yourself (including name, gender, age, and permanent
              location) for financial gain
            </li>
            <li>Physically taking someone&rsquo;s property or assets without their knowledge or permission</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "harassment",
    title: "Sexual Harassment",
    icon: "ri-user-unfollow-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We do not tolerate sexual harassment. We consider sexual harassment to be any non-physical, unwanted, and
          unwelcome sexual behaviors between members. This includes cyberflashing (i.e. sharing unsolicited sexually
          explicit images), in-person indecent exposure, sharing or threatening to share sexual or intimate images
          without the consent of the person involved or depicted, sending unwanted sexual comments or images, and
          fetishization.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            Our mission at Aurays is to foster healthy and equitable relationships through kind connections, so we
            wholly prohibit sexual harassment. We consider sexual harassment to be any non-physical, unwanted, and
            unwelcome sexual behaviors between members. This includes, but isn&rsquo;t limited to:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>Cyberflashing (i.e. sharing unsolicited sexually explicit images)</li>
            <li>Committing in-person indecent exposure (i.e. unwanted flashing on a date)</li>
            <li>
              Obtaining, sharing, or threatening to share sexual images, videos, or screenshots of sexual conversations
              on any website or platform, without the consent of the person involved or depicted (commonly known as
              &ldquo;revenge porn&rdquo; or &ldquo;non-consensual intimate image sharing&rdquo;)
            </li>
            <li>
              Making unwanted sexual comments, questions, and advancements, including sexualized comments about
              someone&rsquo;s body
            </li>
            <li>Fetishizing a member without their consent</li>
            <li>
              Sending unwanted sexualized imagery including memes, animations, drawings, etc., as well as unwanted
              pornographic imagery or links
            </li>
          </ul>
          <p className="mt-4 italic">
            Remember, your off-platform behavior matters too. To protect victims and survivors, we may take action
            against your account for engaging in some of the above sexual harassment behaviors in person, on another
            platform, or over text.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "spam",
    title: "Spam",
    icon: "ri-mail-forbid-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We do not permit any kind of unwanted or irrelevant content sent in bulk or high frequency. This includes
          sharing misleading or misdirecting links, creating an excessive number of accounts causing disruption to other
          members, or having multiple active profiles on our platform to engage in unwanted interactions.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            Spam is any kind of unwanted or irrelevant content usually sent in bulk or high frequency. We don&rsquo;t
            allow:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc">
            <li>Content that&rsquo;s designed to deceive or mislead members in order to increase views or followers</li>
            <li>
              Misleading or misdirecting links including links to websites that pretend to be of a reputable brand or
              service
            </li>
            <li>
              Sharing contact details, private messaging details or sensitive self-identifying information in your
              profile, profile photo or Aurays Date Compliments
            </li>
            <li>Unsolicited promotion of your social media or website in private messages to other members</li>
            <li>Images containing links or QR codes</li>
            <li>Creating an excessive number of accounts causing disruption to other members</li>
            <li>Multiple active profiles on our platform to engage in unwanted interactions</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "self-injury",
    title: "Suicide and Self-Injury Promotion",
    icon: "ri-hand-heart-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We care deeply about our members and understand that some may struggle with mental health, self-injury,
          suicidal thoughts, substance use, or eating disorders. While we do allow members to share personal experiences
          with these issues in a safe way, we don&rsquo;t allow any content that depicts, promotes, glorifies, or
          assists in activities that could lead to suicide, self-injury, or disordered eating or body image.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">
            While we do permit members to share their personal experiences with these issues in a safe way, we do not
            allow any content that depicts, promotes, glorifies, or assists in activities that could lead to suicide,
            self-injury, or disordered eating or body image. We don&rsquo;t allow:
          </p>
          <ul className="flex flex-col gap-2 pl-4 list-disc mb-4">
            <li>Encouraging, promoting, glorifying, or providing instructions for suicide or self-injury</li>
            <li>
              Depicting, promoting, normalizing, glorifying, or providing instructions for disordered eating, dieting,
              or body image.
            </li>
            <li>Graphic imagery depicting self-injury or suicide</li>
            <li>
              Content that&rsquo;s disrespectful toward victims, survivors, or families impacted by suicide,
              self-injury, or eating disorders
            </li>
            <li>Dangerous amateur acts or viral &ldquo;challenges&rdquo; that can cause serious bodily harm</li>
          </ul>
          <p>
            In response to content that may describe a suicidal or self-injurious plan, intent, or thought, we may
            remove the content and send the member local supportive resources such as suicide prevention hotlines. If we
            have reason to believe a member is an imminent danger to themselves, we may contact local emergency
            authorities for intervention, which could include emergency services providing a welfare visit.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "graphic",
    title: "Violent and Graphic Content",
    icon: "ri-eye-off-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We don&rsquo;t permit violent, graphic, or gory content. This includes descriptions of violence in usernames
          or profile content, photos containing real or realistic-appearing blood, bodily fluids, or injury, or images
          depicting guns of any kind (except on a uniformed member of law enforcement or military personnel).
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-3">This includes, but isn&rsquo;t limited to:</p>
          <ul className="flex flex-col gap-2 pl-4 list-disc mb-4">
            <li>
              Imagery containing real or realistic-appearing blood, bodily fluids, violence, or injury. This includes:
              <ul className="flex flex-col gap-1 pl-6 list-circle mt-1">
                <li>Excessive blood that appears to be real</li>
                <li>Graphic injuries or wounds, including those of self-injury, hunting, or food preparation</li>
                <li>Human bones or internal organs</li>
                <li>Fetal remains or miscarriages</li>
                <li>Graphic imagery of medical procedures</li>
                <li>Imagery that shows the violent, shocking, or graphic death or injury of a person or animal</li>
                <li>Imagery containing human remains or corpses</li>
              </ul>
            </li>
            <li>
              Images depicting guns of any kind (except on a uniformed member of law enforcement or military personnel)
            </li>
            <li>General references or descriptions of violence in usernames or profile content</li>
            <li>
              General description of violence or physical injuries in graphic detail including references to dangerous
              organizations and individuals
            </li>
            <li>
              &ldquo;Trophy&rdquo; hunting photos of animals are not allowed unless the image depicts the following
              animals: bison, deer, duck, fish, elk, goose, moose, pheasants, rabbit, turkey, wild boar. However, trophy
              hunting photos depicting the permitted animals above will not be allowed if the photo depicts a gun or
              visible injuries to the animal.
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "manipulation",
    title: "Platform Manipulation",
    icon: "ri-cpu-line",
    content: (
      <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
        <p>
          We foster a community built on genuine connections. To protect our community, we don&rsquo;t allow
          unauthorized automated behaviors that undermine or interfere with our platform. These behaviors can include
          scraping, scripting, or artificially influencing connections or interactions of our member experience,
          including automations that may interact with profiles or messages.
        </p>
        <p>
          We also prohibit ban evasion. To protect our community, we don&rsquo;t allow the creation of new accounts or
          use of VPNs or other methods to circumvent a restriction or ban applied to an account. We remove accounts that
          attempt to circumvent bans when identified.
        </p>
      </div>
    ),
  },
];

export default function CommunityGuidelinesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-[radial-gradient(ellipse_at_top,_#E6D9FF_0%,_transparent_65%)] opacity-40" />
        </div>
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center relative">
          <div className="inline-flex items-center gap-2 bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-shield-user-line"></i>
            Safety First
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-5">Community Guidelines</h1>
          <p className="text-gray-500 text-base leading-relaxed max-w-xl mx-auto">
            Aurays is a space to make kind connections in a safe, inclusive, and respectful way. In order to foster
            healthy and equitable relationships, we hold our members accountable for the way they treat each other.
          </p>
          <p className="text-gray-400 text-sm mt-4 leading-relaxed">
            Our Community Guidelines help keep our members safe. They make it clear what content and conduct is not
            acceptable (on our platform and off).
          </p>
        </div>
      </section>

      {/* Quick nav */}
      <section className="bg-white border-y border-gray-100 py-5 sticky top-[64px] z-30">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400 font-medium mr-1 whitespace-nowrap">Jump to:</span>
            {guidelines.slice(0, 6).map((g) => (
              <span
                key={g.id}
                onClick={() => scrollToSection(g.id)}
                className="text-xs bg-[#F5F0FF] text-[#7C55CC] px-3 py-1 rounded-full cursor-pointer hover:bg-[#E6D9FF] transition-colors whitespace-nowrap"
              >
                {g.title}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="flex flex-col gap-10">
            {guidelines.map((g, index) => (
              <div
                key={index}
                id={g.id}
                className="bg-white rounded-3xl p-8 border border-gray-100 scroll-mt-32 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F5F0FF] flex-shrink-0">
                    <i className={`${g.icon} text-[#7C55CC] text-lg`}></i>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{g.title}</h2>
                </div>
                {g.content}
              </div>
            ))}
          </div>

          {/* Additional Sections from Doc */}
          <div className="flex flex-col gap-10 mt-10">
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F5F0FF] flex-shrink-0">
                  <i className="ri-alarm-warning-line text-[#7C55CC] text-lg"></i>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Safety Reporting</h2>
              </div>
              <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
                <p>
                  Safety is a top priority at Aurays. We use a combination of human moderators and automated systems to
                  monitor and review Aurays accounts and interactions for content that may be against our Community
                  Guidelines, against our Terms and Conditions, or otherwise harmful.
                </p>
                <p>
                  Our members play a critical role in the safety of Aurays by reporting content or behavior that may
                  violate our Community Guidelines. If anything happens that makes you feel uncomfortable or unsafe, we
                  highly encourage you to Unmatch with – or Block & Report – the member. See this article for more info
                  on what happens when you report something to Aurays.
                </p>
                <p>
                  However, please consider that disagreeing with or disliking a member or their content is not
                  necessarily a reason to report them. We may take action against a member if we&rsquo;ve found them to
                  be intentionally creating false or inappropriate reports against other members solely based on their
                  protected attributes. This includes reporting transgender or nonbinary members for no reason other
                  than their gender identity or expression or repeatedly sending false reports of bad behavior.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F5F0FF] flex-shrink-0">
                  <i className="ri-scales-line text-[#7C55CC] text-lg"></i>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Enforcement Philosophy</h2>
              </div>
              <div className="flex flex-col gap-4 text-sm text-gray-500 leading-relaxed">
                <p>
                  All members must comply with the platform rules described and referenced in our Community Guidelines.
                  If you behave in a way that goes against Aurays&rsquo;s Community Guidelines, values, or otherwise act
                  in any way we believe to be potentially harmful to Aurays or its members, we may take a range of
                  actions on your account. When determining the penalty for violating our community guidelines, we
                  consider a number of factors.
                </p>
                <p>For example, we may:</p>
                <ul className="flex flex-col gap-2 pl-4 list-disc">
                  <li>Remove the content</li>
                  <li>Issue a warning</li>
                  <li>Ban the offending member from Aurays</li>
                </ul>
                <p>
                  When necessary, we also may cooperate with law enforcement to assist in potential criminal
                  investigations related to member conduct.
                </p>
                <p>
                  Your treatment towards others outside of the Aurays app can also result in action against your
                  account. If we&rsquo;re made aware of harm between members on dates, meetups with friends, via text
                  message or direct messaging platforms, or relevant alleged criminal or harmful conduct committed in
                  your past or outside of Aurays, we may take action as if it happened on our platform.
                </p>
                <p>
                  If you believe we&rsquo;ve made a mistake in taking action on your account or content, you can always
                  contact us by email at support@aurays.app.
                </p>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-16 bg-white rounded-3xl p-8 border border-gray-100 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Questions or Feedback?</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              If you have any questions or feedback about Aurays&rsquo;s Community Guidelines, please reach out. Our
              support team is always available to help you at support@aurays.app.
            </p>
            <a
              href="mailto:support@aurays.app"
              className="inline-flex items-center gap-2 bg-[#7C55CC] text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-[#5B3FA0] transition-colors"
            >
              <i className="ri-mail-send-line"></i>
              Contact Support
            </a>
          </div>

          {/* Back link */}
          <div className="mt-14 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#7C55CC] hover:text-[#4A2D8A] transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
